import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  ChevronLeft, ChevronRight, Calendar as CalendarIcon, Plus,
  Clock, MapPin, Users, Settings, ArrowLeft, Target
} from 'lucide-react';

const ProjectCalendar = ({ project, onBack }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [events, setEvents] = useState([]);

  // Mock events for demo - different categories and colors like your image
  useEffect(() => {
    setEvents([
      {
        id: '1',
        title: 'Aronia check in',
        date: '2025-09-15',
        time: '6:00 PM',
        category: 'personal',
        color: 'bg-red-400',
        description: 'Weekly health check-in'
      },
      {
        id: '2', 
        title: 'VACO weekly cross training',
        date: '2025-09-15',
        time: '6:00 PM',
        category: 'fitness',
        color: 'bg-gray-400',
        description: 'Volunteer cross-training session'
      },
      {
        id: '3',
        title: 'Hans BD',
        date: '2025-09-19',
        time: '8:00 PM',
        category: 'personal',
        color: 'bg-gray-400',
        description: 'Birthday celebration'
      },
      {
        id: '4',
        title: 'Froee?',
        date: '2025-09-19',
        time: '8:00 PM',
        category: 'social',
        color: 'bg-blue-500',
        description: 'Social gathering'
      },
      {
        id: '5',
        title: 'Defiance weekly jam',
        date: '2025-09-19',
        time: '8:00 PM',
        category: 'music',
        color: 'bg-purple-500',
        description: 'Music session'
      },
      {
        id: '6',
        title: 'Food finance Institute',
        date: '2025-09-18',
        time: '9:00 AM',
        category: 'business',
        color: 'bg-yellow-500',
        description: 'Financial planning'
      },
      {
        id: '7',
        title: 'Second nature third edition',
        date: '2025-09-18',
        time: '3:00 PM',
        category: 'education',
        color: 'bg-yellow-500',
        description: 'Learning session'
      },
      {
        id: '8',
        title: '47h. Pay period STAR...',
        date: '2025-09-21',
        time: '4:00 PM',
        category: 'work',
        color: 'bg-yellow-400',
        description: 'Work milestone'
      },
      {
        id: '9',
        title: 'Froee?',
        date: '2025-09-21',
        time: '8:15 PM',
        category: 'social',
        color: 'bg-blue-500',
        description: 'Social event'
      },
      {
        id: '10',
        title: 'Packers @ Browns',
        date: '2025-09-21',
        time: '12:00 PM',
        category: 'sports',
        color: 'bg-green-500',
        description: 'Game day'
      }
    ]);
  }, []);

  // Get week dates starting from Monday
  const getWeekDays = (date) => {
    const start = new Date(date);
    const day = start.getDay();
    const diff = start.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    start.setDate(diff);
    
    const week = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      week.push(day);
    }
    return week;
  };

  const weekDays = getWeekDays(currentDate);
  const [monday, tuesday, wednesday, thursday, friday, saturday, sunday] = weekDays;

  const getEventsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return events.filter(event => event.date === dateStr);
  };

  const formatTime = (timeStr) => {
    return timeStr; // Already formatted in mock data
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className="h-full bg-black/20 backdrop-blur-xl rounded-2xl border border-white/20 p-6">
      
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <Button
            onClick={onBack}
            variant="outline"
            size="sm"
            className="text-white border-white/40"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          
          <h3 className="text-white text-xl font-bold">
            {project.name} Calendar
          </h3>
        </div>
        
        <div className="flex items-center space-x-3">
          <Button
            onClick={() => setCurrentDate(new Date(currentDate.setDate(currentDate.getDate() - 7)))}
            variant="outline"
            size="sm"
            className="text-white border-white/40"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          
          <span className="text-white font-medium px-4">
            {formatDate(monday)} - {formatDate(sunday)}
          </span>
          
          <Button
            onClick={() => setCurrentDate(new Date(currentDate.setDate(currentDate.getDate() + 7)))}
            variant="outline"
            size="sm"
            className="text-white border-white/40"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
          
          <Button
            onClick={() => alert('Add new event coming soon...')}
            className="bg-green-500 hover:bg-green-600 text-white"
            size="sm"
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Compact Vertical Calendar Grid - Fits in Project Card */}
      <div className="grid grid-cols-2 gap-2 h-80">
        
        {/* Left Side: Monday-Thursday Stacked Vertically - Compact */}
        <div className="grid grid-rows-4 gap-1">
          {[monday, tuesday, wednesday, thursday].map((day, index) => {
            const dayEvents = getEventsForDate(day);
            const dayNames = ['Mon', 'Tue', 'Wed', 'Thu'];
            return (
              <div key={index} className="bg-white/10 rounded-md border border-white/20 p-2 overflow-y-auto">
                
                {/* Compact Day Header */}
                <div className="flex items-center justify-between mb-2 pb-1 border-b border-white/20">
                  <span className="text-white font-bold text-sm">{dayNames[index]} {day.getDate()}</span>
                </div>
                
                {/* Compact Events */}
                <div className="space-y-0.5">
                  {dayEvents.slice(0, 2).map((event) => ( // Limit to 2 events for space
                    <button
                      key={event.id}
                      onClick={() => setSelectedEvent(event)}
                      className={`w-full text-left p-1 rounded text-xs text-white ${event.color} hover:opacity-80 transition-opacity`}
                    >
                      <div className="font-bold text-xs">{event.time}</div>
                      <div className="truncate text-xs">{event.title}</div>
                    </button>
                  ))}
                  {dayEvents.length > 2 && (
                    <div className="text-white/50 text-xs text-center">+{dayEvents.length - 2} more</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Right Side: Friday-Sunday Stacked Vertically - Compact */}
        <div className="grid grid-rows-3 gap-1">
          {[friday, saturday, sunday].map((day, index) => {
            const dayEvents = getEventsForDate(day);
            const dayNames = ['Fri', 'Sat', 'Sun'];
            return (
              <div key={index} className="bg-white/10 rounded-md border border-white/20 p-2 overflow-y-auto">
                
                {/* Compact Day Header */}
                <div className="flex items-center justify-between mb-2 pb-1 border-b border-white/20">
                  <span className="text-white font-bold text-sm">{dayNames[index]} {day.getDate()}</span>
                </div>
                
                {/* Compact Events */}
                <div className="space-y-0.5">
                  {dayEvents.slice(0, 3).map((event) => ( // Limit to 3 events for weekend space
                    <button
                      key={event.id}
                      onClick={() => setSelectedEvent(event)}
                      className={`w-full text-left p-1 rounded text-xs text-white ${event.color} hover:opacity-80 transition-opacity`}
                    >
                      <div className="font-bold text-xs">{event.time}</div>
                      <div className="truncate text-xs">{event.title}</div>
                    </button>
                  ))}
                  {dayEvents.length > 3 && (
                    <div className="text-white/50 text-xs text-center">+{dayEvents.length - 3} more</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Event Details Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-xl flex items-center justify-center z-50">
          <div className="bg-black/90 rounded-2xl border border-white/20 p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-white text-lg font-bold">{selectedEvent.title}</h4>
              <Button
                onClick={() => setSelectedEvent(null)}
                variant="outline"
                size="sm"
                className="text-white border-white/40"
              >
                ✕
              </Button>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <Clock className="w-4 h-4 text-cyan-400" />
                <span className="text-white">{selectedEvent.time}</span>
              </div>
              
              <div className="flex items-center space-x-3">
                <CalendarIcon className="w-4 h-4 text-green-400" />
                <span className="text-white">{new Date(selectedEvent.date).toLocaleDateString()}</span>
              </div>
              
              <div className="flex items-center space-x-3">
                <Target className="w-4 h-4 text-purple-400" />
                <Badge className={`${selectedEvent.color} text-white`}>
                  {selectedEvent.category}
                </Badge>
              </div>
              
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-white/90 text-sm">{selectedEvent.description}</p>
              </div>
              
              <div className="flex space-x-2">
                <Button
                  onClick={() => alert('Edit event coming soon...')}
                  className="flex-1 bg-blue-600/30 text-blue-300 border border-blue-400/50"
                >
                  <Settings className="w-3 h-3 mr-1" />
                  Edit
                </Button>
                <Button
                  onClick={() => alert('Delete event coming soon...')}
                  variant="outline"
                  className="flex-1 text-red-300 border-red-400/50"
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectCalendar;